import { useCallback, useEffect, useMemo, useState } from 'react'
import { EChartsType, EChartsOption, DataZoomComponentOption } from 'echarts'

import { NavigateBefore, NavigateNext } from '@mui/icons-material'

/**
 * Get the count of chart data set.
 * If multiple datasets exist, only the first set is counted.
 *
 * @param echartsInstance ECharts instance
 * @returns data count within chart
 */
const getChartDataCount = (echartsInstance: EChartsType | null) => {
  if (!echartsInstance) {
    return 0
  }

  const options = echartsInstance.getOption() as EChartsOption

  if (options?.series == null) {
    return 0
  }

  const data = Array.isArray(options.series)
    ? (options.series[0]?.data ?? [])
    : options.series.data

  return (data as number[]).length
}

interface IScrollStepperProps {
  showLoading: boolean
  echartsInstance: EChartsType | null
}

/**
 * The component that looks something like this..
 *
 * ------------------------------
 * | Showing X - Y of Z | < | > |
 * ------------------------------
 */
const ScrollStepper = ({
  echartsInstance,
  showLoading
}: IScrollStepperProps) => {
  // NOTE: Cache results to fallback to.
  // During scroll intermediate result may be undefined.
  const [rangeStart, setRangeStart] = useState<number>(1)
  const [rangeEnd, setRangeEnd] = useState<number>(10)

  const dataCount = useMemo(
    () => getChartDataCount(echartsInstance),
    [echartsInstance]
  )

  // TODO: [FE DEBT] move datazoomHandler to handleChartEvents in RiskByLineChart
  const datazoomHandler = useCallback(
    (event: any): void => {
      // startValue and endValue are triggered by hitting the arrows
      if (
        typeof event.startValue === 'number' &&
        typeof event.endValue === 'number'
      ) {
        setRangeStart(event.startValue)
        setRangeEnd(event.endValue)
      } else {
        const options = echartsInstance?.getOption()

        if (options == null) {
          return
        }

        const datazoom = (options.dataZoom as DataZoomComponentOption[])[0]
        // NOTE: Dates are now supported but we don't use time series
        //       so we will assume it's numbers..
        const { startValue, endValue } = datazoom
        const numericStartValue = startValue as number
        const numericEndValue = endValue as number

        if (numericStartValue != null) {
          setRangeStart(numericStartValue)
        }

        if (numericEndValue != null) {
          setRangeEnd(numericEndValue)
        }
      }
    },
    [echartsInstance]
  )

  useEffect(() => {
    if (echartsInstance == null || echartsInstance.isDisposed()) {
      return
    }

    echartsInstance.on('datazoom', datazoomHandler)
    datazoomHandler({})

    return () => {
      if (!echartsInstance.isDisposed()) {
        echartsInstance.off('datazoom', datazoomHandler)
      }
    }
  }, [datazoomHandler, echartsInstance])

  const step = (delta: number): void => {
    echartsInstance?.dispatchAction({
      type: 'dataZoom',
      startValue: rangeStart + delta,
      endValue: rangeEnd + delta
    })
  }

  if (showLoading) {
    return (
      <div className='text-gray-700'>
        <div>Loading...</div>
      </div>
    )
  }

  return (
    <div className='flex items-center text-gray-700'>
      <div className='mr-3 flex text-sm'>
        <span>Showing</span>
        <span className='mx-1 w-4 text-center'>{rangeStart + 1}</span>
        <span>-</span>
        <span className='mx-1 w-4 text-center'>{rangeEnd + 1}</span>
        <span>of</span>
        <span className='mx-1 w-4 text-center'>{dataCount}</span>
      </div>
      <div className='grid grid-cols-2 gap-2'>
        <button
          className={[
            'flex items-center',
            'h-6 w-6',
            'bg-gray-100',
            'disabled:pointer-events-none disabled:opacity-50'
          ].join(' ')}
          disabled={rangeStart < 1}
          onClick={() => step(-1)}
        >
          <NavigateBefore />
        </button>
        <button
          className={[
            'flex items-center',
            'h-6 w-6',
            'bg-gray-100',
            'disabled:pointer-events-none disabled:opacity-50'
          ].join(' ')}
          disabled={rangeEnd + 2 > dataCount}
          onClick={() => step(1)}
        >
          <NavigateNext />
        </button>
      </div>
    </div>
  )
}

export default ScrollStepper
